import { useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { HedgeAnalysis } from '@/lib/hedgeAnalysis';
import { Trophy, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

type StrategyType = 'put' | 'collar' | 'covered';

interface StrategyOption {
  key: string;
  label: string;
  strategy: StrategyType;
  putIndex?: number;
}

interface Props {
  analysis: HedgeAnalysis;
  onClose: () => void;
}

function fmt(v: number): string {
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function computePnL(
  analysis: HedgeAnalysis,
  strategy: StrategyType,
  pricePct: number,
  putIndex: number
): { stockPnL: number; netPnL: number; newPrice: number } {
  const pos = analysis.position;
  const spot = pos.currentPrice;
  const qty = pos.quantity;
  const newPrice = spot * (1 + pricePct / 100);
  const stockPnL = (newPrice - spot) * qty;

  if (strategy === 'put') {
    const put = analysis.protectivePuts[putIndex] || analysis.protectivePuts[0];
    const contracts = put.contractsNeeded;
    const strike = put.strikePrice;
    const putPayoff = Math.max(strike - newPrice, 0) * contracts * 100;
    const strategyPnL = putPayoff - put.totalCost;
    return { stockPnL, netPnL: stockPnL + strategyPnL, newPrice };
  }

  if (strategy === 'collar') {
    const c = analysis.collar;
    const contracts = c.contractsNeeded;
    const putPayoff = Math.max(c.putStrike - newPrice, 0) * contracts * 100;
    const callPayoff = -Math.max(newPrice - c.callStrike, 0) * contracts * 100;
    const premiumCost = c.netPremium > 0 ? c.netPremium : 0;
    const premiumCredit = c.netPremium < 0 ? -c.netPremium : 0;
    const strategyPnL = putPayoff + callPayoff - premiumCost + premiumCredit;
    return { stockPnL, netPnL: stockPnL + strategyPnL, newPrice };
  }

  const cc = analysis.coveredCall;
  const contracts = cc.contractsNeeded;
  const callPayoff = -Math.max(newPrice - cc.strikePrice, 0) * contracts * 100;
  const strategyPnL = cc.incomeGenerated + callPayoff;
  return { stockPnL, netPnL: stockPnL + strategyPnL, newPrice };
}

const STRATEGY_COLORS = {
  A: { stroke: 'hsl(var(--chart-1))', fill: 'hsl(var(--chart-1))' },
  B: { stroke: 'hsl(var(--chart-3))', fill: 'hsl(var(--chart-3))' },
};

export function StrategyCompareChart({ analysis, onClose }: Props) {
  const pos = analysis.position;
  const spot = pos.currentPrice;
  const hasCoveredCall = analysis.coveredCall.contractsNeeded > 0;

  const strategyOptions: StrategyOption[] = useMemo(() => {
    const opts: StrategyOption[] = [];
    analysis.protectivePuts.forEach((put, i) => {
      opts.push({ key: `put-${i}`, label: `Put ${put.protectionLevel}`, strategy: 'put', putIndex: i });
    });
    opts.push({ key: 'collar', label: 'Collar', strategy: 'collar' });
    if (hasCoveredCall) {
      opts.push({ key: 'covered', label: 'Covered Call', strategy: 'covered' });
    }
    return opts;
  }, [analysis, hasCoveredCall]);

  const [stratA, setStratA] = useState(strategyOptions[0]?.key || '');
  const [stratB, setStratB] = useState(strategyOptions.length > 1 ? strategyOptions[1].key : strategyOptions[0]?.key || '');
  const [sliderValue, setSliderValue] = useState([0]);
  const pricePct = sliderValue[0];

  const optionA = strategyOptions.find(o => o.key === stratA) || strategyOptions[0];
  const optionB = strategyOptions.find(o => o.key === stratB) || strategyOptions[1] || strategyOptions[0];

  const chartData = useMemo(() => {
    const data = [];
    for (let pct = -100; pct <= 100; pct += 2) {
      const calcA = computePnL(analysis, optionA.strategy, pct, optionA.putIndex ?? 0);
      const calcB = computePnL(analysis, optionB.strategy, pct, optionB.putIndex ?? 0);
      const netA = Math.round(calcA.netPnL);
      const netB = Math.round(calcB.netPnL);
      data.push({
        pct,
        price: Math.round(calcA.newPrice * 100) / 100,
        stockOnly: Math.round(calcA.stockPnL),
        stratA: netA,
        stratB: netB,
        profitA: netA >= 0 ? netA : 0,
        lossA: netA < 0 ? netA : 0,
        profitB: netB >= 0 ? netB : 0,
        lossB: netB < 0 ? netB : 0,
      });
    }
    return data;
  }, [analysis, optionA, optionB]);

  const currentA = useMemo(() => computePnL(analysis, optionA.strategy, pricePct, optionA.putIndex ?? 0), [analysis, optionA, pricePct]);
  const currentB = useMemo(() => computePnL(analysis, optionB.strategy, pricePct, optionB.putIndex ?? 0), [analysis, optionB, pricePct]);

  const diff = currentA.netPnL - currentB.netPnL;

  // Compute scenario analysis: which strategy wins in each zone
  const scenarioAnalysis = useMemo(() => {
    const scenarios = [
      { label: 'Crash', range: '-50% to -30%', pcts: [-50, -40, -30] },
      { label: 'Bear', range: '-30% to -10%', pcts: [-30, -20, -10] },
      { label: 'Flat', range: '-10% to +10%', pcts: [-10, -5, 0, 5, 10] },
      { label: 'Bull', range: '+10% to +30%', pcts: [10, 20, 30] },
      { label: 'Rally', range: '+30% to +50%', pcts: [30, 40, 50] },
    ];
    return scenarios.map(s => {
      let aWins = 0, bWins = 0;
      s.pcts.forEach(pct => {
        const a = computePnL(analysis, optionA.strategy, pct, optionA.putIndex ?? 0).netPnL;
        const b = computePnL(analysis, optionB.strategy, pct, optionB.putIndex ?? 0).netPnL;
        if (a > b) aWins++;
        else if (b > a) bWins++;
      });
      return { ...s, winner: aWins > bWins ? 'A' as const : bWins > aWins ? 'B' as const : 'tie' as const };
    });
  }, [analysis, optionA, optionB]);

  // Weighted overall recommendation: sum net P&L across all scenarios
  const overallRecommendation = useMemo(() => {
    // Weight: Crash/Rally=1, Bear/Bull=2, Flat=3 (most likely scenarios weigh more)
    const weights: Record<string, number> = { Crash: 1, Bear: 2, Flat: 3, Bull: 2, Rally: 1 };
    let totalA = 0, totalB = 0, totalWeight = 0;
    const allPcts = [-50, -40, -30, -20, -10, -5, 0, 5, 10, 20, 30, 40, 50];
    const getWeight = (pct: number) => {
      if (pct <= -30) return weights.Crash;
      if (pct <= -10) return weights.Bear;
      if (pct <= 10) return weights.Flat;
      if (pct <= 30) return weights.Bull;
      return weights.Rally;
    };
    allPcts.forEach(pct => {
      const w = getWeight(pct);
      const a = computePnL(analysis, optionA.strategy, pct, optionA.putIndex ?? 0).netPnL;
      const b = computePnL(analysis, optionB.strategy, pct, optionB.putIndex ?? 0).netPnL;
      totalA += a * w;
      totalB += b * w;
      totalWeight += w;
    });
    const avgA = totalA / totalWeight;
    const avgB = totalB / totalWeight;
    const aWinScenarios = scenarioAnalysis.filter(s => s.winner === 'A').length;
    const bWinScenarios = scenarioAnalysis.filter(s => s.winner === 'B').length;
    const winner = avgA > avgB ? 'A' as const : avgB > avgA ? 'B' as const : 'tie' as const;
    const margin = Math.abs(avgA - avgB);
    const confidence = margin > 1000 ? 'High' : margin > 200 ? 'Medium' : 'Low';
    return { winner, avgA, avgB, margin, confidence, aWinScenarios, bWinScenarios };
  }, [analysis, optionA, optionB, scenarioAnalysis]);

  const currentWinner = diff > 0 ? 'A' : diff < 0 ? 'B' : 'tie';
  const winnerLabel = currentWinner === 'A' ? optionA.label : currentWinner === 'B' ? optionB.label : 'Tie';

  return (
    <Card className="mt-4 border-2 border-accent/30 bg-gradient-to-br from-background to-accent/5">
      <CardContent className="pt-5 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Badge variant="outline" className="text-xs font-bold bg-accent/10 text-accent-foreground border-accent/30">
            ⚔️ Strategy Comparison — {pos.ticker}
          </Badge>
          <button onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
            ✕ Close
          </button>
        </div>

        {/* Strategy selectors */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-xs font-semibold" style={{ color: STRATEGY_COLORS.A.stroke }}>Strategy A</label>
            <Select value={stratA} onValueChange={setStratA}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {strategyOptions.map(o => (
                  <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold" style={{ color: STRATEGY_COLORS.B.stroke }}>Strategy B</label>
            <Select value={stratB} onValueChange={setStratB}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {strategyOptions.map(o => (
                  <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Chart */}
        <div className="h-[280px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={`cmpProfitA-${pos.ticker}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(142 71% 45%)" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="hsl(142 71% 45%)" stopOpacity={0.03} />
                </linearGradient>
                <linearGradient id={`cmpLossA-${pos.ticker}`} x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="hsl(0 84% 60%)" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="hsl(0 84% 60%)" stopOpacity={0.03} />
                </linearGradient>
                <linearGradient id={`cmpProfitB-${pos.ticker}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(142 71% 45%)" stopOpacity={0.15} />
                  <stop offset="100%" stopColor="hsl(142 71% 45%)" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id={`cmpLossB-${pos.ticker}`} x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="hsl(0 84% 60%)" stopOpacity={0.15} />
                  <stop offset="100%" stopColor="hsl(0 84% 60%)" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
              <XAxis
                dataKey="pct"
                tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={(v) => `${v}%`}
                interval={9}
              />
              <YAxis
                tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0]?.payload;
                  return (
                    <div className="bg-popover border border-border rounded-lg p-3 shadow-lg text-xs space-y-1">
                      <p className="font-bold text-foreground">Price: ${fmt(d.price)} ({d.pct >= 0 ? '+' : ''}{d.pct}%)</p>
                      <p className="text-muted-foreground">Stock Only: <span className={d.stockOnly >= 0 ? 'text-green-500' : 'text-red-500'}>${fmt(d.stockOnly)}</span></p>
                      <p style={{ color: STRATEGY_COLORS.A.stroke }} className="font-semibold">
                        {optionA.label}: <span className={d.stratA >= 0 ? 'text-green-500' : 'text-red-500'}>${fmt(d.stratA)}</span>
                      </p>
                      <p style={{ color: STRATEGY_COLORS.B.stroke }} className="font-semibold">
                        {optionB.label}: <span className={d.stratB >= 0 ? 'text-green-500' : 'text-red-500'}>${fmt(d.stratB)}</span>
                      </p>
                      <p className="text-muted-foreground border-t border-border pt-1">
                        Difference: <span className={d.stratA - d.stratB >= 0 ? 'text-green-500' : 'text-red-500'}>${fmt(d.stratA - d.stratB)}</span>
                      </p>
                    </div>
                  );
                }}
              />
              <ReferenceLine y={0} stroke="hsl(var(--foreground))" strokeWidth={1.5} strokeDasharray="4 4" />
              <ReferenceLine
                x={pricePct}
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                strokeDasharray="3 3"
              />
              <Area type="monotone" dataKey="stockOnly" stroke="hsl(var(--muted-foreground))" strokeWidth={1} strokeDasharray="5 5" fill="transparent" name="Stock Only" />
              <Area type="monotone" dataKey="profitA" stroke="none" strokeWidth={0} fill={`url(#cmpProfitA-${pos.ticker})`} name="Profit A" legendType="none" />
              <Area type="monotone" dataKey="lossA" stroke="none" strokeWidth={0} fill={`url(#cmpLossA-${pos.ticker})`} name="Loss A" legendType="none" />
              <Area type="monotone" dataKey="profitB" stroke="none" strokeWidth={0} fill={`url(#cmpProfitB-${pos.ticker})`} name="Profit B" legendType="none" />
              <Area type="monotone" dataKey="lossB" stroke="none" strokeWidth={0} fill={`url(#cmpLossB-${pos.ticker})`} name="Loss B" legendType="none" />
              <Area type="monotone" dataKey="stratA" stroke={STRATEGY_COLORS.A.stroke} strokeWidth={2.5} fill="transparent" name={optionA.label} />
              <Area type="monotone" dataKey="stratB" stroke={STRATEGY_COLORS.B.stroke} strokeWidth={2.5} fill="transparent" name={optionB.label} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-5 text-xs flex-wrap">
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-[2px] border-t-2 border-dashed" style={{ borderColor: 'hsl(var(--muted-foreground))' }} />
            <span className="text-muted-foreground">Stock Only</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-[3px] rounded" style={{ background: STRATEGY_COLORS.A.stroke }} />
            <span className="font-medium" style={{ color: STRATEGY_COLORS.A.stroke }}>{optionA.label}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-[3px] rounded" style={{ background: STRATEGY_COLORS.B.stroke }} />
            <span className="font-medium" style={{ color: STRATEGY_COLORS.B.stroke }}>{optionB.label}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-3 rounded-sm" style={{ background: 'hsla(142, 71%, 45%, 0.25)' }} />
            <span className="text-green-600 dark:text-green-400 font-medium">Profit</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-3 rounded-sm" style={{ background: 'hsla(0, 84%, 60%, 0.25)' }} />
            <span className="text-red-600 dark:text-red-400 font-medium">Loss</span>
          </div>
        </div>

        {/* Slider */}
        <div className="space-y-2 px-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>-100%</span>
            <span className="font-bold text-foreground text-sm">
              Price Move: {pricePct >= 0 ? '+' : ''}{pricePct}%
            </span>
            <span>+100%</span>
          </div>
          <Slider
            value={sliderValue}
            onValueChange={setSliderValue}
            min={-100}
            max={100}
            step={1}
            className="w-full"
          />
        </div>

        {/* Comparison Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="p-3 rounded-lg bg-muted/50 text-center">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">New Price</p>
            <p className="text-lg font-bold text-foreground">${fmt(currentA.newPrice)}</p>
          </div>
          <div className="p-3 rounded-lg bg-muted/50 text-center">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Stock P&L</p>
            <p className={cn("text-lg font-bold", currentA.stockPnL >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
              ${fmt(currentA.stockPnL)}
            </p>
          </div>
          <div className="p-3 rounded-lg text-center border" style={{ borderColor: STRATEGY_COLORS.A.stroke + '40' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: STRATEGY_COLORS.A.stroke }}>{optionA.label}</p>
            <p className={cn("text-lg font-bold", currentA.netPnL >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
              ${fmt(currentA.netPnL)}
            </p>
          </div>
          <div className="p-3 rounded-lg text-center border" style={{ borderColor: STRATEGY_COLORS.B.stroke + '40' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: STRATEGY_COLORS.B.stroke }}>{optionB.label}</p>
            <p className={cn("text-lg font-bold", currentB.netPnL >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
              ${fmt(currentB.netPnL)}
            </p>
          </div>
          <div className={cn("p-3 rounded-lg text-center border-2", diff >= 0 ? 'border-green-500/30 bg-green-500/10' : 'border-red-500/30 bg-red-500/10')}>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">A vs B Δ</p>
            <p className={cn("text-lg font-bold", diff >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
              ${fmt(diff)}
            </p>
          </div>
        </div>

        {/* Best Strategy Badge */}
        <div className={cn(
          "p-4 rounded-lg border-2 flex items-center gap-3",
          currentWinner === 'A' ? 'border-[hsl(var(--chart-1))]/30 bg-[hsl(var(--chart-1))]/5' :
          currentWinner === 'B' ? 'border-[hsl(var(--chart-3))]/30 bg-[hsl(var(--chart-3))]/5' :
          'border-border bg-muted/30'
        )}>
          <Trophy className={cn("h-5 w-5 shrink-0", currentWinner === 'tie' ? 'text-muted-foreground' : 'text-yellow-500')} />
          <div className="flex-1">
            <p className="text-xs text-muted-foreground">Best Strategy at {pricePct >= 0 ? '+' : ''}{pricePct}% move</p>
            <p className="text-sm font-bold text-foreground">
              🏆 {winnerLabel}
              {currentWinner !== 'tie' && (
                <span className="text-green-600 dark:text-green-400 ml-2">
                  saves ${fmt(Math.abs(diff))} more
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Scenario Analysis Table */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Winner by Market Scenario</p>
          <div className="grid grid-cols-5 gap-2">
            {scenarioAnalysis.map(s => {
              const icon = s.label === 'Crash' || s.label === 'Bear' ? TrendingDown :
                           s.label === 'Bull' || s.label === 'Rally' ? TrendingUp : Minus;
              const Icon = icon;
              const winColor = s.winner === 'A' ? STRATEGY_COLORS.A.stroke :
                               s.winner === 'B' ? STRATEGY_COLORS.B.stroke : 'hsl(var(--muted-foreground))';
              return (
                <div key={s.label} className="p-2 rounded-lg bg-muted/40 text-center space-y-1">
                  <div className="flex items-center justify-center gap-1">
                    <Icon className="h-3 w-3 text-muted-foreground" />
                    <span className="text-[10px] font-semibold text-muted-foreground">{s.label}</span>
                  </div>
                  <p className="text-[9px] text-muted-foreground">{s.range}</p>
                  <Badge variant="outline" className="text-[10px] font-bold border-0 px-1.5" style={{ color: winColor }}>
                    {s.winner === 'A' ? optionA.label : s.winner === 'B' ? optionB.label : 'Tie'}
                  </Badge>
                </div>
              );
            })}
          </div>
        </div>

        {/* Overall Recommendation */}
        <div className={cn(
          "p-4 rounded-xl border-2 space-y-3",
          overallRecommendation.winner === 'A' ? 'border-[hsl(var(--chart-1))]/40 bg-[hsl(var(--chart-1))]/5' :
          overallRecommendation.winner === 'B' ? 'border-[hsl(var(--chart-3))]/40 bg-[hsl(var(--chart-3))]/5' :
          'border-border bg-muted/20'
        )}>
          <div className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            <span className="text-sm font-bold text-foreground">Overall Recommendation</span>
            <Badge variant="outline" className={cn(
              "text-[10px] ml-auto",
              overallRecommendation.confidence === 'High' ? 'border-green-500/40 text-green-600 dark:text-green-400' :
              overallRecommendation.confidence === 'Medium' ? 'border-yellow-500/40 text-yellow-600 dark:text-yellow-400' :
              'border-muted-foreground/40 text-muted-foreground'
            )}>
              {overallRecommendation.confidence} Confidence
            </Badge>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-extrabold" style={{
              color: overallRecommendation.winner === 'A' ? STRATEGY_COLORS.A.stroke :
                     overallRecommendation.winner === 'B' ? STRATEGY_COLORS.B.stroke : 'hsl(var(--muted-foreground))'
            }}>
              🏆 {overallRecommendation.winner === 'A' ? optionA.label : overallRecommendation.winner === 'B' ? optionB.label : 'Tie — Both equal'}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div className="p-2 rounded-lg bg-muted/40 text-center">
              <p className="text-[10px] text-muted-foreground uppercase">Weighted Avg P&L</p>
              <p className="font-bold" style={{ color: STRATEGY_COLORS.A.stroke }}>{optionA.label}</p>
              <p className={cn("font-bold", overallRecommendation.avgA >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
                ${fmt(overallRecommendation.avgA)}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-muted/40 text-center">
              <p className="text-[10px] text-muted-foreground uppercase">Weighted Avg P&L</p>
              <p className="font-bold" style={{ color: STRATEGY_COLORS.B.stroke }}>{optionB.label}</p>
              <p className={cn("font-bold", overallRecommendation.avgB >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
                ${fmt(overallRecommendation.avgB)}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-muted/40 text-center">
              <p className="text-[10px] text-muted-foreground uppercase">Scenarios Won</p>
              <p className="font-bold text-foreground">
                <span style={{ color: STRATEGY_COLORS.A.stroke }}>{overallRecommendation.aWinScenarios}</span>
                {' vs '}
                <span style={{ color: STRATEGY_COLORS.B.stroke }}>{overallRecommendation.bWinScenarios}</span>
              </p>
              <p className="text-[10px] text-muted-foreground">of 5 scenarios</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
